import { CartLinesUpdateEvent, CartErrorEvent } from "@shopify/events";

/**
 * <add-to-cart> progressively enhances a wrapped `<form action="/cart/add">`.
 *
 * It's theme-agnostic: it only adds to the cart and reports the outcome through
 * standard events. Cart UI updates happen in `shopify:cart:lines-update`
 * listeners (Horizon's cart components, or cart-event-bridge.js in Dawn-based
 * themes).
 *
 * The event's promise resolves with Horizon's `detail` contract:
 * `{ source, sourceId, variantId, lineKey, itemCount, items, sections, didError }`.
 * Failed requests reject the promise and dispatch `shopify:cart:error`.
 *
 * Attributes:
 * - `context`: standard event context, `product` (default), `cart` or `dialog`.
 *
 * Sets `aria-busy="true"` on itself while a request is in flight.
 */
class AddToCart extends HTMLElement {
  #busy = false;

  connectedCallback() {
    this.form = this.querySelector("form");
    this.form?.addEventListener("submit", this.#onSubmit);
  }

  disconnectedCallback() {
    this.form?.removeEventListener("submit", this.#onSubmit);
  }

  get context() {
    return this.getAttribute("context") || "product";
  }

  #onSubmit = (event) => {
    event.preventDefault();
    if (this.#busy) return;

    this.add();
  };

  async add() {
    const formData = new FormData(this.form);
    const variantId = formData.get("id");
    const quantity = Number(formData.get("quantity")) || 1;
    const sectionIds = this.#sectionIds();
    const deferred = CartLinesUpdateEvent.createPromise();
    const detail = {
      source: "add-to-cart",
      sourceId: this.id,
      variantId,
      itemCount: quantity,
    };

    this.#setBusy(true);

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: "add",
        context: this.context,
        lines: [{ merchandiseId: variantId, quantity }],
        promise: deferred.promise,
        detail,
      }),
    );

    if (sectionIds.length) {
      formData.append("sections", sectionIds.join(","));
      formData.append("sections_url", window.location.pathname);
    }

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add`, {
        method: "POST",
        headers: {
          Accept: "application/javascript",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: formData,
      });
      const parsedState = await response.json();
      const ajaxCart = await this.#fetchCart();
      const cart = CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart);

      // The cart declined the add (sold out, quantity limits, etc.): resolve with the unchanged cart
      if (parsedState.status) {
        deferred.resolve({
          cart,
          userErrors: [
            {
              field: ["lines"],
              message: parsedState.description || parsedState.message,
            },
          ],
          detail: {
            ...detail,
            items: ajaxCart.items,
            didError: true,
            errors: parsedState.errors || parsedState.description,
            message: parsedState.message,
          },
        });
        return;
      }

      deferred.resolve({
        cart,
        detail: {
          ...detail,
          lineKey: parsedState.key,
          items: ajaxCart.items,
          sections: parsedState.sections,
          didError: false,
        },
      });
    } catch (error) {
      console.error(error);

      this.dispatchEvent(
        new CartErrorEvent({
          error: error.message,
          code: "SERVICE_UNAVAILABLE",
        }),
      );
      deferred.reject(error);
    } finally {
      this.#setBusy(false);
    }
  }

  /**
   * Sections to render in the add request, so listeners can update without refetching.
   */
  #sectionIds() {
    // Horizon: each cart-items-component re-renders from its own section
    const ids = [
      ...document.querySelectorAll("cart-items-component[data-section-id]"),
    ].map((element) => element.dataset.sectionId);

    // Dawn: the cart drawer / notification declares the sections it needs
    const dawnCart =
      document.querySelector("cart-drawer") ||
      document.querySelector("cart-notification");
    if (typeof dawnCart?.getSectionsToRender === "function") {
      ids.push(...dawnCart.getSectionsToRender().map((section) => section.id));
    }

    return [...new Set(ids)];
  }

  async #fetchCart() {
    const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
      headers: { Accept: "application/json" },
    });
    if (!response.ok)
      throw new Error(`Cart request failed with status ${response.status}`);

    return response.json();
  }

  #setBusy(busy) {
    this.#busy = busy;
    const submitButtons = this.form.querySelectorAll('[type="submit"]');

    if (busy) {
      this.setAttribute("aria-busy", "true");
      submitButtons.forEach((button) =>
        button.setAttribute("aria-disabled", "true"),
      );
    } else {
      this.removeAttribute("aria-busy");
      submitButtons.forEach((button) =>
        button.removeAttribute("aria-disabled"),
      );
    }
  }
}

if (!customElements.get("add-to-cart")) {
  customElements.define("add-to-cart", AddToCart);
}
